import { Injectable } from '@angular/core';
import { DataGridColumn } from '../interfaces/builder-screen.models';

export interface OptimizedAnalyzedField {
  name: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'textarea';
  dataPath: string;
  label: string;
  isNested: boolean;
  nestedFields?: OptimizedAnalyzedField[];
  isArray?: boolean;
  sampleValue?: any;
  arrayColumns?: DataGridColumn[];
  arrayData?: any[];
}

export interface OptimizedAnalyzedEntity {
  name: string;
  dataPath: string;
  fields: OptimizedAnalyzedField[];
  sampleData: any[];
  suggestedColumns: DataGridColumn[];
  level: number;
  parentEntity?: string;
  isPrimitiveArray?: boolean;
  originalFieldName?: string;
  parentContext?: string;
  // NOVO: Propriedades otimizadas
  importance: 'high' | 'medium' | 'low'; // Importância baseada em heurísticas
  recordCount: number; // Número de registros no array
  complexity: number; // Complexidade estrutural (0-10)
  suggestedGrouping?: string; // Sugestão de agrupamento (qual tab pai)
}

export interface OptimizedScreenAnalysisResult {
  entities: OptimizedAnalyzedEntity[];
  suggestedLayout: {
    tabs: { id: string; name: string; description?: string; }[];
    panels: { id: string; name: string; tab: string; order: number; }[];
  };
  dataSourcePath: string;
  // NOVO: Metadados da análise
  metadata: {
    totalArrays: number;
    totalFields: number;
    maxDepth: number;
    suggestedStrategy: 'single-tab' | 'multi-tab' | 'hierarchical';
    warnings: string[];
  };
}

/**
 * ⭐ NOVO: Serviço OTIMIZADO de análise de JSON para geração de telas
 * 
 * Melhorias sobre o serviço original:
 * 1. Análise de importância e relevância dos dados
 * 2. Agrupamento inteligente baseado em contexto semântico
 * 3. Redução de tabs desnecessárias
 * 4. Melhor organização hierárquica
 * 5. Sugestões baseadas em boas práticas de UX
 */
@Injectable({ providedIn: 'root' })
export class OptimizedScreenAnalyzerService {

  /**
   * Analisa JSON com estratégia otimizada
   */
  analyzeJSON(json: any): OptimizedScreenAnalysisResult {
    console.log('🚀 [OTIMIZADO] Analisando JSON:', json);

    // 1. Encontrar todas as entidades (arrays) recursivamente
    const rawEntities = this.findAllArrays(json);
    console.log(`📊 [OTIMIZADO] Encontradas ${rawEntities.length} entidades:`, 
      rawEntities.map(e => `${e.name} (nível ${e.level}, ${e.data.length} registros)`));

    // 2. Analisar e enriquecer cada entidade com metadados otimizados
    const analyzedEntities = rawEntities.map(entity => this.analyzeEntityOptimized(entity));

    // 3. Calcular estratégia de layout baseada na análise
    const strategy = this.determineLayoutStrategy(analyzedEntities);
    console.log(`🎯 [OTIMIZADO] Estratégia sugerida: ${strategy}`);

    // 4. Gerar layout otimizado baseado na estratégia
    const suggestedLayout = this.generateOptimizedLayout(analyzedEntities, strategy);

    // 5. Calcular metadados da análise
    const metadata = this.calculateMetadata(analyzedEntities, rawEntities, strategy);

    return {
      entities: analyzedEntities,
      suggestedLayout,
      dataSourcePath: this.findRootDataPath(json),
      metadata
    };
  }

  /**
   * ⭐ NOVO: Encontra todos os arrays recursivamente com mais contexto
   */
  private findAllArrays(
    obj: any,
    path: string = '',
    level: number = 0,
    parentEntity?: string,
    parentContextPath: string = ''
  ): Array<{
    name: string;
    path: string;
    data: any[];
    level: number;
    parentEntity?: string;
    isPrimitiveArray?: boolean;
    originalFieldName?: string;
    parentContext?: string;
  }> {
    const arrays: any[] = [];

    const search = (current: any, currentPath: string, currentLevel: number, currentParent?: string, currentContextPath: string = '') => {
      if (!current || typeof current !== 'object') return;

      for (const key in current) {
        const value = current[key];
        const newPath = currentPath ? `${currentPath}.${key}` : key;
        const newContextPath = currentContextPath ? `${currentContextPath}.${key}` : key;

        // Array de objetos = entidade
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
          const entityName = this.formatEntityName(key);
          
          arrays.push({
            name: entityName,
            path: newPath,
            data: value,
            level: currentLevel,
            parentEntity: currentParent,
            contextPath: newContextPath
          });

          // Continuar buscando arrays dentro deste array
          search(value[0], newPath, currentLevel + 1, entityName, newContextPath);
        }
        // Array de primitivos
        else if (Array.isArray(value) && value.length > 0) {
          const entityName = this.formatEntityName(key);
          const pathParts = newPath.split('.');
          
          // FIX: Usar o contexto completo (tudo menos o nome do array e 'response')
          // Ex: "response.item_list.image.image_id_list" -> contexto = "item_list.image"
          let parentContext = '';
          if (pathParts.length > 2) {
            // Remover 'response' do início e o nome do campo do final
            const contextParts = pathParts.slice(1, -1); // Remove primeiro e último
            parentContext = contextParts.join('.');
          }
          
          // Transformar em array de objetos
          const transformedData = value.map((item: any, index: number) => ({
            index: index,
            value: item
          }));
          
          arrays.push({
            name: entityName,
            path: newPath,
            data: transformedData,
            level: currentLevel,
            parentEntity: currentParent,
            isPrimitiveArray: true,
            originalFieldName: key,
            parentContext: parentContext,
            contextPath: newContextPath
          });
        }
        // Objeto: continuar buscando
        else if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
          search(value, newPath, currentLevel, currentParent, newContextPath);
        }
      }
    };

    search(obj, path, level, parentEntity, parentContextPath);
    return arrays;
  }

  /**
   * ⭐ NOVO: Analisa entidade com avaliação de importância e complexidade
   */
  private analyzeEntityOptimized(entity: any): OptimizedAnalyzedEntity {
    const firstItem = entity.data[0];
    
    // Analisar campos
    const fields = entity.isPrimitiveArray 
      ? this.createPrimitiveArrayFields(entity)
      : this.analyzeFields(firstItem, entity.path);

    // Gerar colunas
    const columns = entity.isPrimitiveArray
      ? this.createPrimitiveArrayColumns(entity)
      : this.generateColumns(fields);

    // Calcular importância baseada em heurísticas
    const importance = this.calculateImportance(entity, fields);

    // Calcular complexidade estrutural
    const complexity = this.calculateComplexity(fields, entity.data.length);

    // Sugerir agrupamento
    const suggestedGrouping = this.suggestGrouping(entity, importance);

    return {
      name: entity.name,
      dataPath: entity.path,
      fields,
      sampleData: entity.data,
      suggestedColumns: columns,
      level: entity.level,
      parentEntity: entity.parentEntity,
      isPrimitiveArray: entity.isPrimitiveArray,
      originalFieldName: entity.originalFieldName,
      parentContext: entity.parentContext,
      // Novos campos otimizados
      importance,
      recordCount: entity.data.length,
      complexity,
      suggestedGrouping
    };
  }

  /**
   * ⭐ NOVO: Calcula importância de uma entidade baseada em heurísticas
   */
  private calculateImportance(entity: any, fields: OptimizedAnalyzedField[]): 'high' | 'medium' | 'low' {
    let score = 0;

    // Heurística 1: Nível de aninhamento (raiz = mais importante)
    if (entity.level === 0) score += 10;
    else if (entity.level === 1) score += 5;

    // Heurística 2: Número de registros (mais registros = mais importante)
    if (entity.data.length > 5) score += 5;
    else if (entity.data.length > 2) score += 3;

    // Heurística 3: Número de campos (mais campos = mais importante)
    if (fields.length > 10) score += 5;
    else if (fields.length > 5) score += 3;

    // Heurística 4: Tipo de array (objetos > primitivos)
    if (!entity.isPrimitiveArray) score += 5;

    // Heurística 5: Nome sugere entidade principal
    const mainKeywords = ['list', 'items', 'data', 'records', 'main', 'principal'];
    if (mainKeywords.some(kw => entity.name.toLowerCase().includes(kw))) {
      score += 5;
    }

    // Classificar baseado no score
    if (score >= 15) return 'high';
    if (score >= 8) return 'medium';
    return 'low';
  }

  /**
   * ⭐ NOVO: Calcula complexidade estrutural (0-10)
   */
  private calculateComplexity(fields: OptimizedAnalyzedField[], recordCount: number): number {
    let complexity = 0;

    // Fator 1: Número de campos
    complexity += Math.min(fields.length / 3, 3); // Máximo 3 pontos

    // Fator 2: Campos aninhados
    const nestedCount = fields.filter(f => f.isNested).length;
    complexity += Math.min(nestedCount, 3); // Máximo 3 pontos

    // Fator 3: Arrays aninhados
    const arrayCount = fields.filter(f => f.isArray).length;
    complexity += Math.min(arrayCount * 1.5, 3); // Máximo 3 pontos

    // Fator 4: Volume de dados
    if (recordCount > 100) complexity += 1;

    return Math.min(Math.round(complexity), 10);
  }

  /**
   * ⭐ NOVO: Sugere agrupamento inteligente para a entidade
   */
  private suggestGrouping(entity: any, importance: string): string | undefined {
    // Entidades de alta importância ficam em tabs próprias
    if (importance === 'high' && entity.level <= 1) {
      return 'own-tab';
    }

    // Entidades de baixa importância em nível 2+ ficam agrupadas com o pai
    if (importance === 'low' && entity.level >= 2) {
      return entity.parentEntity || 'main';
    }

    // Arrays de primitivos sempre agrupam com o contexto pai
    if (entity.isPrimitiveArray) {
      return entity.parentContext || entity.parentEntity || 'main';
    }

    return undefined;
  }

  /**
   * ⭐ NOVO: Determina estratégia de layout baseada na análise
   */
  private determineLayoutStrategy(entities: OptimizedAnalyzedEntity[]): 'single-tab' | 'multi-tab' | 'hierarchical' {
    const highImportance = entities.filter(e => e.importance === 'high').length;
    const mediumImportance = entities.filter(e => e.importance === 'medium').length;
    const totalComplexity = entities.reduce((sum, e) => sum + e.complexity, 0);
    const maxLevel = Math.max(...entities.map(e => e.level));

    // Estratégia 1: SINGLE-TAB (tudo em uma tab)
    // Quando: Poucas entidades, baixa complexidade
    if (entities.length <= 3 && totalComplexity < 10) {
      return 'single-tab';
    }

    // Estratégia 2: MULTI-TAB (várias tabs no mesmo nível)
    // Quando: Várias entidades importantes, complexidade média
    if (highImportance >= 2 && maxLevel <= 2) {
      return 'multi-tab';
    }

    // Estratégia 3: HIERARCHICAL (tabs hierárquicas com agrupamentos)
    // Quando: Muitas entidades, estrutura profunda, alta complexidade
    return 'hierarchical';
  }

  /**
   * ⭐ NOVO: Gera layout otimizado baseado na estratégia
   * (Público para permitir re-geração com diferentes estratégias)
   */
  generateOptimizedLayout(
    entities: OptimizedAnalyzedEntity[],
    strategy: 'single-tab' | 'multi-tab' | 'hierarchical'
  ): {
    tabs: { id: string; name: string; description?: string; }[];
    panels: { id: string; name: string; tab: string; order: number; }[];
  } {
    const tabs: any[] = [];
    const panels: any[] = [];

    switch (strategy) {
      case 'single-tab':
        return this.generateSingleTabLayout(entities);
      
      case 'multi-tab':
        return this.generateMultiTabLayout(entities);
      
      case 'hierarchical':
        return this.generateHierarchicalLayout(entities);
      
      default:
        return this.generateSingleTabLayout(entities);
    }
  }

  /**
   * ⭐ NOVO: Gera nome único considerando duplicatas em toda a lista
   */
  private generatePanelNameWithContext(entity: OptimizedAnalyzedEntity, allEntities: OptimizedAnalyzedEntity[]): string {
    // Verificar se há outras entidades com o mesmo nome
    const duplicates = allEntities.filter(e => e.name === entity.name);
    
    if (duplicates.length > 1) {
      // Há duplicatas! Adicionar contexto pai para diferenciar
      const parentFromPath = this.extractParentFromPath(entity.dataPath);
      const parentLabel = parentFromPath ? this.formatLabel(parentFromPath) : '';
      
      console.log(`🔄 Entidade duplicada "${entity.name}" - adicionando contexto: "${parentLabel}"`);
      
      return parentLabel 
        ? `${parentLabel} - ${entity.name}`
        : `Lista de ${entity.name}`;
    } else {
      // Única entidade com esse nome
      return `Lista de ${entity.name}`;
    }
  }
  
  /**
   * ⭐ Helper: Extrai o pai imediato do dataPath
   * Ex: "response.item_list.image.image_id_list" -> "image"
   * Ex: "response.item_list.promotion_image.image_id_list" -> "promotion_image"
   */
  private extractParentFromPath(dataPath: string): string {
    const parts = dataPath.split('.');
    if (parts.length > 2) {
      // Pegar o penúltimo elemento (antes do nome da entidade)
      return parts[parts.length - 2];
    }
    return '';
  }

  /**
   * ⭐ Helper: Gera nome descritivo e único para painel (método antigo, mantido para retrocompatibilidade)
   */
  private generatePanelName(entity: OptimizedAnalyzedEntity): string {
    if (entity.isPrimitiveArray) {
      // Para arrays de primitivos, incluir contexto pai completo
      const contextLabel = entity.parentContext 
        ? this.formatLabel(entity.parentContext) 
        : '';
      const fieldLabel = entity.originalFieldName 
        ? this.formatLabel(entity.originalFieldName) 
        : entity.name;
      
      return contextLabel 
        ? `${contextLabel} - ${fieldLabel}` 
        : `Lista de ${fieldLabel}`;
    } else {
      return `Lista de ${entity.name}`;
    }
  }

  /**
   * Layout SINGLE-TAB: Tudo em uma única tab
   * ⭐ Melhoria: Gera nomes únicos e descritivos para arrays de primitivos
   */
  private generateSingleTabLayout(entities: OptimizedAnalyzedEntity[]): any {
    const rootEntity = entities.find(e => e.level === 0) || entities[0];
    const tabId = `tab_${rootEntity.name.toLowerCase()}_main`;

    const tabs = [{
      id: tabId,
      name: rootEntity.name,
      description: 'Visualização completa de todos os dados'
    }];

    const panels = entities.map((entity, index) => {
      const panelName = this.generatePanelNameWithContext(entity, entities);
      
      return {
        id: `panel_${entity.name.toLowerCase()}_${index}`,
        name: panelName,
        tab: tabId,
        order: index + 1
      };
    });

    return { tabs, panels };
  }

  /**
   * Layout MULTI-TAB: Tabs separadas para entidades importantes
   */
  private generateMultiTabLayout(entities: OptimizedAnalyzedEntity[]): any {
    const tabs: any[] = [];
    const panels: any[] = [];
    
    // Agrupar por nível e importância
    const rootEntities = entities.filter(e => e.level === 0);
    const level1Entities = entities.filter(e => e.level === 1 && e.importance !== 'low');
    const otherEntities = entities.filter(e => !rootEntities.includes(e) && !level1Entities.includes(e));

    // Tab principal para entidade raiz
    if (rootEntities.length > 0) {
      const root = rootEntities[0];
      const tabId = `tab_${root.name.toLowerCase()}_main`;
      
      tabs.push({
        id: tabId,
        name: root.name,
        description: 'Dados principais'
      });

      panels.push({
        id: `${tabId}_grid`,
        name: `Lista de ${root.name}`,
        tab: tabId,
        order: 1
      });

      // Adicionar entidades de baixa importância relacionadas à raiz
      otherEntities
        .filter(e => e.level === 1 || e.parentEntity === root.name)
        .forEach((entity, index) => {
          panels.push({
            id: `panel_${entity.name.toLowerCase()}_${index}`,
            name: this.generatePanelNameWithContext(entity, entities),
            tab: tabId,
            order: panels.filter(p => p.tab === tabId).length + 1
          });
        });
    }

    // Tabs separadas para entidades de nível 1 importantes
    level1Entities.forEach((entity, entityIndex) => {
      // FIX: Detectar duplicatas e adicionar contexto ao nome da TAB
      const duplicates = level1Entities.filter(e => e.name === entity.name);
      const tabName = duplicates.length > 1
        ? this.generatePanelNameWithContext(entity, entities).replace('Lista de ', '') // Remove "Lista de"
        : entity.name;
      
      // FIX: ID único incluindo índice para evitar colisão
      const tabId = `tab_${entity.name.toLowerCase()}_${entityIndex}`;
      
      tabs.push({
        id: tabId,
        name: tabName,
        description: `Detalhes de ${tabName}`
      });

      panels.push({
        id: `${tabId}_grid`,
        name: this.generatePanelNameWithContext(entity, entities),
        tab: tabId,
        order: 1
      });

      // Adicionar entidades filhas nesta tab
      const children = otherEntities.filter(e => e.parentEntity === entity.name);
      children.forEach((child, index) => {
        panels.push({
          id: `panel_${child.name.toLowerCase()}_${index}`,
          name: this.generatePanelNameWithContext(child, entities),
          tab: tabId,
          order: index + 2
        });
      });
    });

    return { tabs, panels };
  }

  /**
   * Layout HIERARCHICAL: Agrupamento inteligente com hierarquia
   */
  private generateHierarchicalLayout(entities: OptimizedAnalyzedEntity[]): any {
    const tabs: any[] = [];
    const panels: any[] = [];

    // Agrupar por nível
    const level0 = entities.filter(e => e.level === 0);
    const level1High = entities.filter(e => e.level === 1 && e.importance === 'high' && !e.isPrimitiveArray);
    const level1Rest = entities.filter(e => e.level === 1 && (e.importance !== 'high' || e.isPrimitiveArray));
    const level2Plus = entities.filter(e => e.level >= 2);

    // Tab raiz
    if (level0.length > 0) {
      const root = level0[0];
      const rootTabId = `tab_${root.name.toLowerCase()}_main`;
      
      tabs.push({
        id: rootTabId,
        name: root.name,
        description: 'Visão principal'
      });

      // Panel para o grid raiz
      panels.push({
        id: `${rootTabId}_grid`,
        name: `Lista de ${root.name}`,
        tab: rootTabId,
        order: 1
      });

      // Adicionar entidades de nível 1 de baixa importância aqui
      level1Rest.forEach((entity, index) => {
        panels.push({
          id: `panel_${entity.name.toLowerCase()}_${index}`,
          name: this.generatePanelNameWithContext(entity, entities),
          tab: rootTabId,
          order: index + 2
        });
      });
    }

    // Tabs para entidades de nível 1 de alta importância
    level1High.forEach((entity, entityIndex) => {
      // FIX: Detectar duplicatas e adicionar contexto ao nome da TAB
      const duplicates = level1High.filter(e => e.name === entity.name);
      const tabName = duplicates.length > 1
        ? this.generatePanelNameWithContext(entity, entities).replace('Lista de ', '') // Remove "Lista de"
        : entity.name;
      
      // FIX: ID único incluindo índice para evitar colisão
      const tabId = `tab_${level0[0].name.toLowerCase()}_${entity.name.toLowerCase()}_${entityIndex}`;
      
      tabs.push({
        id: tabId,
        name: tabName,
        description: `Gerenciar ${tabName.toLowerCase()}`
      });

      panels.push({
        id: `${tabId}_grid`,
        name: this.generatePanelNameWithContext(entity, entities),
        tab: tabId,
        order: 1
      });

      // Adicionar entidades de nível 2+ relacionadas
      const children = level2Plus.filter(e => e.parentEntity === entity.name);
      children.forEach((child, index) => {
        panels.push({
          id: `panel_${child.name.toLowerCase()}_${index}`,
          name: this.generatePanelNameWithContext(child, entities),
          tab: tabId,
          order: index + 2
        });
      });
    });

    return { tabs, panels };
  }

  // ===== Métodos auxiliares =====

  private createPrimitiveArrayFields(entity: any): OptimizedAnalyzedField[] {
    return [
      {
        name: 'index',
        type: 'number',
        dataPath: 'index',
        label: '#',
        isNested: false
      },
      {
        name: 'value',
        type: typeof entity.data[0]?.value === 'number' ? 'number' : 'text',
        dataPath: 'value',
        label: this.formatLabel(entity.originalFieldName || entity.name),
        isNested: false
      }
    ];
  }

  private createPrimitiveArrayColumns(entity: any): DataGridColumn[] {
    return [
      {
        field: 'index',
        header: '#',
        type: 'number',
        dataPath: 'index',
        width: '80px',
        sortable: true,
        filterable: false,
        editable: false
      },
      {
        field: 'value',
        header: this.formatLabel(entity.originalFieldName?.replace('_list', '') || entity.name),
        type: typeof entity.data[0]?.value === 'number' ? 'number' : 'text',
        dataPath: 'value',
        width: '300px',
        sortable: true,
        filterable: true,
        editable: true
      }
    ];
  }

  private analyzeFields(obj: any, parentPath: string = ''): OptimizedAnalyzedField[] {
    const fields: OptimizedAnalyzedField[] = [];

    for (const key in obj) {
      const value = obj[key];
      const dataPath = key;

      // Objeto aninhado
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        const nestedFields = this.analyzeFields(value, dataPath);
        
        // Achatar objetos pequenos
        if (nestedFields.length <= 3) {
          nestedFields.forEach(nf => {
            fields.push({
              name: `${key}_${nf.name}`,
              type: nf.type,
              dataPath: dataPath,
              label: `${this.formatLabel(key)} - ${nf.label}`,
              isNested: false
            });
          });
        } else {
          fields.push({
            name: key,
            type: 'text',
            dataPath: dataPath,
            label: this.formatLabel(key),
            isNested: true,
            nestedFields: nestedFields,
            sampleValue: value
          });
        }
      }
      // Array - não adicionar como campo (será entidade separada)
      else if (Array.isArray(value)) {
        // Ignorar arrays
      }
      // Campo primitivo
      else {
        fields.push({
          name: key,
          type: this.inferFieldType(key, value),
          dataPath: dataPath,
          label: this.formatLabel(key),
          isNested: false,
          sampleValue: value
        });
      }
    }

    return fields;
  }

  private generateColumns(fields: OptimizedAnalyzedField[]): DataGridColumn[] {
    return fields
      .filter(f => !f.isArray)
      .map(field => ({
        field: field.name,
        header: field.label,
        type: this.mapToGridType(field.type),
        dataPath: field.dataPath,
        width: this.suggestColumnWidth(field.type),
        sortable: true,
        filterable: field.type === 'text' || field.type === 'select',
        editable: field.type !== 'date'
      }));
  }

  private calculateMetadata(
    analyzedEntities: OptimizedAnalyzedEntity[],
    rawEntities: any[],
    strategy: string
  ): any {
    const maxDepth = Math.max(...analyzedEntities.map(e => e.level), 0);
    const totalFields = analyzedEntities.reduce((sum, e) => sum + e.fields.length, 0);
    const warnings: string[] = [];

    // Gerar avisos úteis
    if (analyzedEntities.length > 10) {
      warnings.push('Muitas entidades detectadas. Considere simplificar a estrutura de dados.');
    }

    if (maxDepth > 3) {
      warnings.push('Estrutura muito profunda. Isso pode dificultar a navegação.');
    }

    const highComplexityEntities = analyzedEntities.filter(e => e.complexity >= 7);
    if (highComplexityEntities.length > 0) {
      warnings.push(`${highComplexityEntities.length} entidade(s) com alta complexidade detectada(s).`);
    }

    return {
      totalArrays: rawEntities.length,
      totalFields,
      maxDepth,
      suggestedStrategy: strategy,
      warnings
    };
  }

  private inferFieldType(fieldName: string, value: any): 'text' | 'number' | 'date' | 'boolean' | 'textarea' {
    const name = fieldName.toLowerCase();

    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    
    if (name.includes('date') || name.includes('time') || name.includes('createdat') || name.includes('updatedat')) {
      return 'date';
    }
    
    if (name.includes('description') || name.includes('notes') || name.includes('comment')) {
      return 'textarea';
    }
    
    if (typeof value === 'string' && value.length > 100) {
      return 'textarea';
    }
    
    return 'text';
  }

  private mapToGridType(fieldType: string): 'text' | 'number' | 'date' | 'boolean' {
    if (fieldType === 'textarea' || fieldType === 'select') return 'text';
    return fieldType as 'text' | 'number' | 'date' | 'boolean';
  }

  private suggestColumnWidth(type: string): string {
    switch (type) {
      case 'boolean': return '100px';
      case 'number': return '120px';
      case 'date': return '150px';
      case 'textarea': return '300px';
      default: return '200px';
    }
  }

  private formatEntityName(key: string): string {
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  private formatLabel(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .trim();
  }

  private findRootDataPath(json: any): string {
    const keys = Object.keys(json);
    return keys.length > 0 ? keys[0] : '';
  }
}

